import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';

import Routes from 'routing/Routes';
import CollectionDetails from 'collections/components/CollectionDetails';
import { Button, ButtonToolbar, Row, Alert } from 'components/bootstrap';
import { DocumentTitle, Icon, IfPermitted, PageHeader, ShareButton, Spinner } from 'components/common';
import useParams from 'routing/useParams';
import useCollection from 'collections/hooks/useCollection';
import EntityShareModal from 'components/permissions/EntityShareModal';
import { COLLECTIONS_QUERY_KEY } from 'collections/hooks/useCollections';
import UpdateCollectionButton from 'collections/components/UpdateCollectionButton';

const HeaderTitle = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings.sm};
    margin-bottom: ${theme.spacings.xs};
  `,
);

const StyledInfoList = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.xs};
    margin-bottom: ${theme.spacings.xs};
    margin-left: ${theme.spacings.xs};
    list-style-type: none;
  `,
);

const CollectionDetailsPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ collectionId: string }>();
  const [showCollectionShareModal, setShowCollectionShareModal] = useState<boolean>(false);
  const toggleCollectionShareModal = () => setShowCollectionShareModal(!showCollectionShareModal);
  const { data: collection, isLoading } = useCollection(params.collectionId, { enabled: !!params.collectionId });
  const collectionTitle = collection?.title ? `Collection ${collection.title}` : 'Collection';

  const refetchCollection = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [COLLECTIONS_QUERY_KEY, collection?.id],
      }),
    [queryClient, collection?.id],
  );

  const handleShareClose = () => {
    refetchCollection();

    toggleCollectionShareModal();
  };

  return (
    <DocumentTitle title={collectionTitle}>
      <PageHeader
        title={
          <HeaderTitle>
            <Button onClick={() => navigate(Routes.pluginRoute('COLLECTIONS'))}>
              <Icon name="arrow_left_alt" size="sm" /> Back
            </Button>
            {collectionTitle}
          </HeaderTitle>
        }
        topActions={
          collection && (
            <ButtonToolbar>
              <IfPermitted permissions={`collections:edit:${collection.id}`}>
                <UpdateCollectionButton editCollection={collection} buttonText="Edit" />
              </IfPermitted>
              <ShareButton
                entityId={collection.id}
                entityType="collection"
                onClick={toggleCollectionShareModal}
                title="Manage"
              />
            </ButtonToolbar>
          )
        }>
        {collection?.description ? (
          <>
            <span>{collection?.description}</span> <br />
            <Alert bsStyle="info" title="Page Information">
              <StyledInfoList>
                <li>
                  The content, membership, management and configuration of a Collection can be managed from this page.
                </li>
                <li>Sharing Collection Content with a user grants them permission to the sharable entities within.</li>
                <li>
                  To modify the content of this Collection, or the permission level the Collection grants to content, a
                  user must first be granted permission to Manage that Collection.
                </li>
              </StyledInfoList>
            </Alert>
          </>
        ) : (
          <Alert bsStyle="info">
            <StyledInfoList>
              <li>
                The content, membership, management and configuration of a Collection can be managed from this page.
              </li>
              <li>Sharing Collection Content with a user grants them permission to the sharable entities within.</li>
              <li>
                To modify the content of this Collection, or the permission level the Collection grants to content, a
                user must first be granted permission to Manage that Collection.
              </li>
            </StyledInfoList>
          </Alert>
        )}
      </PageHeader>

      {isLoading && (
        <Row className="content">
          <Spinner text="Loading collection..." />
        </Row>
      )}
      {collection && (
        <>
          <CollectionDetails collection={collection} />
          {showCollectionShareModal && (
            <EntityShareModal
              entityId={collection.id}
              entityType="collection"
              entityTitle={collection.title}
              description="Search for a User or Team to add as collaborator on this Collection."
              onClose={handleShareClose}
            />
          )}
        </>
      )}
    </DocumentTitle>
  );
};

export default CollectionDetailsPage;
